import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BriefcaseBusiness,
  Cake,
  CircleAlert,
  ExternalLink,
  FileText,
  Gift,
  HeartHandshake,
  Mail,
  Microscope,
  Newspaper,
  Plus,
  RefreshCw,
  Stethoscope,
  UserRound,
  Users,
} from "lucide-react";
import { useAuthStore } from "../../context/AuthContext";
import { hasPermission } from "../../lib/utils/permissions";
import { requestHandler } from "../../lib/utils/network-client";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import BirthdayCelebration from "./BirthdayCelebration";

const EMPTY_DASHBOARD = {
  total_users: 0,
  total_employees: 0,
  total_doctors: 0,
  total_specialities: 0,
  total_symptoms: 0,
  total_blogs: 0,
  total_news: 0,
  total_career_applications: 0,
  total_contact_requests: 0,
  attention: {
    pending_applications: 0,
    new_contact_requests: 0,
    missing_doctor_seo: 0,
    missing_speciality_seo: 0,
    missing_blog_seo: 0,
    missing_news_seo: 0,
    incomplete_doctors: 0,
    incomplete_symptoms: 0,
  },
  latest_applications: [],
  latest_contact_requests: [],
  todays_birthdays: [],
};

const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000/";

const parseApiDate = (value) => {
  if (!value) return null;
  const parsed = new Date(
    typeof value === "string" ? value.replace(" ", "T") : value,
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value, options = {}) => {
  const date = parseApiDate(value);
  if (!date) return "Date unavailable";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...options,
  });
};

const getInitials = (firstName, lastName) =>
  `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase() ||
  "U";

const statusClasses = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  reviewing: "bg-blue-50 text-blue-700 border-blue-200",
  shortlisted: "bg-violet-50 text-violet-700 border-violet-200",
  hired: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

const contactStatusClasses = {
  new: "bg-amber-50 text-amber-700 border-amber-200",
  read: "bg-blue-50 text-blue-700 border-blue-200",
  replied: "bg-violet-50 text-violet-700 border-violet-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const DashboardSkeleton = () => (
  <div className="max-w-[1600px] space-y-6 pb-8">
    <Skeleton className="h-48 w-full rounded-lg" />
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <Skeleton key={item} className="h-28 rounded-lg" />
      ))}
    </div>
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <Skeleton className="h-96 rounded-lg" />
      <Skeleton className="h-96 rounded-lg" />
    </div>
  </div>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex min-h-52 flex-col items-center justify-center px-6 text-center">
    <div className="mb-3 rounded-lg bg-slate-100 p-3 text-slate-400">
      <Icon className="h-6 w-6" />
    </div>
    <p className="font-semibold text-slate-700">{title}</p>
    <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>
  </div>
);

const Dashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showBirthdayCelebration, setShowBirthdayCelebration] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await requestHandler("/dashboard", { method: "GET" });
      if (!response?.success || !response.data) {
        throw new Error(response?.message || "Unable to load dashboard data.");
      }
      setDashboard({
        ...EMPTY_DASHBOARD,
        ...response.data,
        attention: {
          ...EMPTY_DASHBOARD.attention,
          ...(response.data.attention || {}),
        },
        latest_applications: response.data.latest_applications || [],
        latest_contact_requests: response.data.latest_contact_requests || [],
        todays_birthdays: response.data.todays_birthdays || [],
      });
    } catch (error) {
      setErrorMessage(error.message || "Unable to load dashboard data.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!user || dashboard.todays_birthdays.length === 0) return;
    const isMyBirthday = dashboard.todays_birthdays.some(
      (birthday) =>
        (birthday.user_id && String(birthday.user_id) === String(user.id)) ||
        birthday.email === user.email,
    );
    setShowBirthdayCelebration(isMyBirthday);
  }, [dashboard.todays_birthdays, user]);

  const quickActions = useMemo(
    () =>
      [
        {
          label: "Add doctor",
          path: "/doctors/new",
          module: "doctors",
          icon: Stethoscope,
        },
        {
          label: "Add speciality",
          path: "/specialities/new",
          module: "specialities",
          icon: Microscope,
        },
        {
          label: "Create blog",
          path: "/blogs/new",
          module: "blogs",
          icon: Newspaper,
        },
        {
          label: "Create news",
          path: "/news/new",
          module: "news",
          icon: FileText,
        },
        {
          label: "Add health camp",
          path: "/health-camps/new",
          module: "health-camps",
          icon: HeartHandshake,
        },
      ].filter((action) => hasPermission(user, action.module, "create")),
    [user],
  );

  const overviewCards = useMemo(
    () =>
      [
        {
          label: "Doctors",
          value: dashboard.total_doctors,
          path: "/doctors",
          module: "doctors",
          icon: UserRound,
          color: "bg-emerald-50 text-emerald-700",
        },
        {
          label: "Specialities",
          value: dashboard.total_specialities,
          path: "/specialities",
          module: "specialities",
          icon: Microscope,
          color: "bg-cyan-50 text-cyan-700",
        },
        {
          label: "Symptoms",
          value: dashboard.total_symptoms,
          path: "/symptoms",
          module: "symptoms",
          icon: HeartHandshake,
          color: "bg-rose-50 text-rose-700",
        },
        {
          label: "Blogs",
          value: dashboard.total_blogs,
          path: "/blogs",
          module: "blogs",
          icon: Newspaper,
          color: "bg-amber-50 text-amber-700",
        },
        {
          label: "News",
          value: dashboard.total_news,
          path: "/news",
          module: "news",
          icon: FileText,
          color: "bg-violet-50 text-violet-700",
        },
        {
          label: "Users",
          value: dashboard.total_users,
          path: "/users",
          module: "users",
          icon: Users,
          color: "bg-blue-50 text-blue-700",
        },
      ].filter((card) => hasPermission(user, card.module, "list")),
    [dashboard, user],
  );

  if (isLoading) return <DashboardSkeleton />;

  if (errorMessage) {
    return (
      <div className="max-w-[1600px]">
        <div className="flex min-h-[420px] flex-col items-center justify-center rounded-lg border border-rose-100 bg-white p-8 text-center shadow-sm">
          <div className="mb-4 rounded-lg bg-rose-50 p-3 text-rose-600">
            <CircleAlert className="h-7 w-7" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Dashboard could not load</h1>
          <p className="mt-2 max-w-md text-sm text-slate-500">{errorMessage}</p>
          <Button type="button" className="mt-5 gap-2 rounded-lg" onClick={fetchDashboard}>
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const firstName = user?.first_name
    ? `${user.first_name.charAt(0).toUpperCase()}${user.first_name.slice(1)}`
    : "User";
  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const canViewApplications = hasPermission(user, "career", "list");
  const canViewContactRequests = hasPermission(user, "contact-requests", "list");

  return (
    <div className="max-w-[1600px] space-y-6 pb-8">
      <section className="relative overflow-hidden rounded-lg border border-emerald-900/10 bg-gradient-to-br from-[#145a49] via-[#1d745c] to-[#258c70] p-6 text-white shadow-md shadow-emerald-950/10 sm:p-8">
        <div className="absolute -right-16 -top-24 h-72 w-72 rounded-full bg-emerald-200/10 blur-2xl" />
        <div className="absolute -bottom-28 left-1/3 h-60 w-60 rounded-full bg-white/10 blur-2xl" />
        <div className="relative z-10 flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold text-emerald-100">{today}</p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
              Welcome back, {firstName}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-emerald-50 sm:text-base">
              Manage website content and keep up with the latest admin activity.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {quickActions.slice(0, 3).map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.path}
                  type="button"
                  onClick={() => navigate(action.path)}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/25 bg-white/10 px-3.5 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
                >
                  <Plus className="h-4 w-4" />
                  <Icon className="hidden h-4 w-4 sm:block" />
                  {action.label}
                </button>
              );
            })}
            <a
              href={WEBSITE_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-white px-4 text-sm font-semibold text-primary transition hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-white/70"
            >
              View website
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {quickActions.length > 3 && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-2 text-sm font-semibold text-slate-700">Quick actions</span>
            {quickActions.slice(3).map((action) => {
              const Icon = action.icon;
              return (
                <Button
                  key={action.path}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2 rounded-lg"
                  onClick={() => navigate(action.path)}
                >
                  <Icon className="h-4 w-4 text-primary" />
                  {action.label}
                </Button>
              );
            })}
          </div>
        </section>
      )}

      {overviewCards.length > 0 && (
        <section>
          <div className="mb-3">
            <h2 className="text-lg font-bold text-slate-900">Content overview</h2>
            <p className="text-sm text-slate-500">Current records available on the website.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {overviewCards.map((card) => {
              const Icon = card.icon;
              return (
                <button
                  key={card.label}
                  type="button"
                  onClick={() => navigate(card.path)}
                  className="group flex min-h-28 flex-col justify-between rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-primary/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`rounded-lg p-2 ${card.color}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <ArrowRight className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-primary" />
                  </div>
                  <div className="mt-3">
                    <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                    <p className="text-sm font-medium text-slate-500">{card.label}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {(canViewApplications || canViewContactRequests) && (
        <section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          {canViewContactRequests && (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-cyan-50 p-2 text-cyan-700">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-900">Contact Us Requests</h2>
                    <p className="text-xs text-slate-500">
                      {dashboard.total_contact_requests} total inquiries
                      {dashboard.attention.new_contact_requests > 0
                        ? ` · ${dashboard.attention.new_contact_requests} new`
                        : " · No new inquiries"}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1 rounded-lg text-primary"
                  onClick={() => navigate("/contact-requests")}
                >
                  View all <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              {dashboard.latest_contact_requests.length === 0 ? (
                <EmptyState
                  icon={Mail}
                  title="No contact requests yet"
                  description="New Contact Us inquiries will appear here."
                />
              ) : (
                <div className="divide-y divide-slate-100">
                  {dashboard.latest_contact_requests.map((request) => (
                    <button
                      key={request.id}
                      type="button"
                      onClick={() => navigate("/contact-requests")}
                      className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/40"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-50 text-xs font-bold text-cyan-700">
                        {request.name?.charAt(0)?.toUpperCase() || "C"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-800">{request.name}</p>
                        <p className="mt-0.5 truncate text-xs text-slate-500">
                          {request.message || request.email}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold capitalize ${contactStatusClasses[request.status] || "border-slate-200 bg-slate-50 text-slate-600"}`}>
                          {request.status}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          {formatDate(request.created_at, { year: undefined })}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {canViewApplications && (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-amber-50 p-2 text-amber-700">
                    <BriefcaseBusiness className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-900">Career Applications</h2>
                    <p className="text-xs text-slate-500">
                      {dashboard.total_career_applications} total career applications
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1 rounded-lg text-primary"
                  onClick={() => navigate("/career/applications")}
                >
                  View all <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              {dashboard.latest_applications.length === 0 ? (
                <EmptyState
                  icon={BriefcaseBusiness}
                  title="No applications yet"
                  description="New career applications will appear here."
                />
              ) : (
                <div className="divide-y divide-slate-100">
                  {dashboard.latest_applications.map((application) => (
                    <button
                      key={application.id}
                      type="button"
                      onClick={() => navigate("/career/applications")}
                      className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/40"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-xs font-bold text-amber-700">
                        {application.name?.charAt(0)?.toUpperCase() || "A"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-800">{application.name}</p>
                        <p className="mt-0.5 truncate text-xs text-slate-500">
                          {application.position}
                          {application.city ? ` · ${application.city}` : ""}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold capitalize ${statusClasses[application.status] || "border-slate-200 bg-slate-50 text-slate-600"}`}>
                          {application.status}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          {formatDate(application.created_at, { year: undefined })}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {dashboard.todays_birthdays.length > 0 && (
        <section className="overflow-hidden rounded-lg border border-pink-100 bg-gradient-to-r from-rose-50 to-pink-50 p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-white p-2.5 text-rose-500 shadow-sm">
                <Cake className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-bold text-slate-900">Today&apos;s birthdays</h2>
                <p className="text-xs text-slate-500">Wish your teammates a wonderful day.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {dashboard.todays_birthdays.map((birthday) => (
                <div key={birthday.id || birthday.user_id} className="flex items-center gap-2 rounded-lg border border-white bg-white/80 px-3 py-2 shadow-sm">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-100 text-xs font-bold text-rose-600">
                    {getInitials(birthday.first_name, birthday.last_name)}
                  </div>
                  <span className="text-sm font-semibold text-slate-700">
                    {birthday.first_name} {birthday.last_name}
                  </span>
                  <Gift className="h-4 w-4 text-rose-400" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <BirthdayCelebration
        isOpen={showBirthdayCelebration}
        onClose={() => setShowBirthdayCelebration(false)}
      />
    </div>
  );
};

export default Dashboard;
